import type { RecoilState, RecoilValue, Snapshot } from "recoil"
import {
  DefaultValue,
  atom,
  atomFamily,
  selector,
  useRecoilCallback,
} from "recoil"

import type { RecoilStateFamily } from "~/components/ui"
import { diff } from "~/utils/diff"

export const loadContents = <T>(
  snapshot: Snapshot,
  recoilState: RecoilState<T>,
): T => {
  return snapshot.getLoadable<T>(recoilState).contents
}

export const getFrom =
  (snapshot: Snapshot) =>
  <T>(recoilState: RecoilState<T> | RecoilValue<T>): T =>
    snapshot.getLoadable<T>(recoilState).contents

export const selectObject = <S, K extends string>(
  key: string,
  findState: RecoilStateFamily<S, K>,
  index: RecoilState<K[]>,
): RecoilState<Record<K, S>> => {
  const flatState = selector<Record<K, S>>({
    key: `${key}Object`,
    get: ({ get }) =>
      get(index).reduce(
        (acc, id) => ({ ...acc, [id]: get(findState(id)) }),
        {},
      ) as Record<K, S>,
    set: ({ get, set }, newValue) => {
      if (newValue instanceof DefaultValue) return set(index, [])
      const oldValue = get(flatState)
      const delta = diff(oldValue, newValue)
      if (delta) {
        const idSet = new Set(get(index))
        Object.entries(delta).forEach(([id, value]) => {
          if (!value) {
            idSet.delete(id as K)
          } else {
            set(findState(id as K), value as S)
            idSet.add(id as K)
          }
        })
        set(index, [...idSet])
      }
    },
  })

  return flatState as RecoilState<Record<K, S>>
}

type MoleculeOptions<S> = {
  key: string
  default: S | (() => S)
}

export type Multitude<S, K extends string = string> = [
  findState: RecoilStateFamily<S, K>,
  index: RecoilState<K[]>,
  objectState: RecoilState<Record<K, S>>,
]

// [eyecue-codemap:aX9GTsQdZBQ]
export const molecule = <T, K extends string>({
  key,
  default: defaultOption,
}: MoleculeOptions<T>): Multitude<T, K> => {
  const index = atom<K[]>({
    key: `${key}Index`,
    default: [],
  })
  const findState = atomFamily<T, K>({
    key,
    default: defaultOption,
  })
  const objectState = selectObject(`${key}_design`, findState, index)

  return [findState, index, objectState]
}

interface IRecoilMapperOptions<D, S, K> {
  makeKey(datum: D): K
  makeState(datum: D): S
  findState: RecoilStateFamily<S, K>
  index: RecoilState<K[]>
}

export type RecoilMapper<D> = (data: D[]) => void

export const createRecoilMapper =
  <DATA, STATE, KEY>({
    makeKey,
    makeState,
    findState,
    index,
  }: IRecoilMapperOptions<DATA, STATE, KEY>): (() => RecoilMapper<DATA>) =>
  () =>
    useRecoilCallback(({ set }) => (data) => {
      const keys = data.map(makeKey)
      // reset(index)
      set(index, keys)
      data.forEach((datum, idx) => set(findState(keys[idx]), makeState(datum)))
    })

export const selectDiff = <T extends object | null>(
  localState: RecoilState<T>,
  remoteState: RecoilState<T>,
): RecoilValue<Partial<T> | null> =>
  selector<Partial<T> | null>({
    key: `${localState.key}Diff`,
    get: ({ get }) => {
      const local = get(localState)
      const remote = get(remoteState)

      return diff(remote ?? {}, local ?? {})
    },
  })

export const selectPresence = <T>(
  recoilState: RecoilValue<Array<T>>,
): RecoilValue<boolean> =>
  selector<boolean>({
    key: `${recoilState.key}Presence`,
    get: ({ get }) => {
      const state = get(recoilState)

      return state.length > 0
    },
  })
